import * as Sentry from '@sentry/browser';
import { prop } from 'micro-fp';
import { shouldImpersonate } from '@sdk/authentication/impersonate';
import { getIntercomBootSettings } from 'sling-sdk/src/intercom/getIntercomBootSettings';
import intercomScript, { onIntercomScriptLoad } from 'sling-sdk/src/intercom/intercomScript';

import { getIntercomBootSettingsFlags } from '@web/common/intercom/getIntercomBootSettingsFlags';
import getUserRoleName from '@web/common/utils/getUserRoleName';

import config from '../../config';
import { INTERCOM_AUTOACTIVATE_PERIOD } from '../../constants';

// Observability sampling for the legacy-Sling-workspace-selection signal (ECPL-6501). The legacy
// Messenger is still selected on web whenever the Toast flag resolves false (or its JWT fetch
// fails); at production volume an unsampled captureMessage per selection would blow the Sentry
// error budget (see src/app.js), so we emit for only this fraction. Multiply Sentry counts by
// 1/this rate (≈50× at 0.02) to estimate true legacy-selection volume.
const INTERCOM_LEGACY_BOOT_SAMPLE_RATE = 0.02;

// Tokens last 24h (support-tools IntercomHashService.generateIntercomJWT, as of 2026-10-05);
// refresh an hour ahead of exp to cover a boot/update or messenger open racing expiry and a
// device clock running up to this far behind the server's.
const INTERCOM_JWT_EXPIRY_BUFFER_MS = 60 * 60 * 1000;

const INTERCOM_JWT_REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

export default class IntercomService {
  constructor(Authentication, Utils, $window, $sessionStorage, $document, $q, $injector, uuid) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Utils,
      $window,
      $sessionStorage,
      $document,
      $q,
      $injector,
      uuid,
    });
    this.active = false;
    this.intercomSettings = {
      app_id: config.INTERCOM_APP_ID,
    };
  }

  get intercom() {
    // if therer is no Intercom on window (dev env) just return a dummy function to avoid JS errors
    return this.$window.Intercom || (() => {}); // eslint-disable-line new-cap
  }

  shouldAutoactivate() {
    const { registeredInIntercom } =
      this.Authentication.user && this.Authentication.user.settings.clientFlags;
    const when = new Date(registeredInIntercom);
    return registeredInIntercom === true || new Date() - when < INTERCOM_AUTOACTIVATE_PERIOD;
  }

  activate() {
    if (!this.intercomSettings.app_id) {
      return;
    }

    if (this.$window.__slingIntercomBooted) {
      return;
    }

    if (!this.active) {
      this.active = true;
      this.$window.__slingIntercomBooted = true;
      if (this._isJwtExpired(this.intercomSettings.intercom_user_jwt)) {
        void this._refreshJwt().finally(() => this._boot());
      } else {
        this._boot();
      }

      /**
       * This flag used to be boolean, now it's a timestamp. So if it's an old true boolean
       * just give it a fresh timestamp and save.
       */
      if (this.Authentication.user?.settings.clientFlags.registeredInIntercom === true) {
        this.Authentication.user.settings.clientFlags.registeredInIntercom = new Date();
        this.Authentication.syncSettings();
      }
    }
  }

  _boot() {
    this.intercom('shutdown');
    this.intercom('boot', this.intercomSettings);
    if (this.intercomSettings.intercom_user_jwt) {
      // A tab can outlive the 24h token without ever re-booting, so check it again when the
      // user actually opens the chat.
      this.intercom('onShow', () => this._refreshJwtIfExpiring());
      this._listenForTabReturn();
    }
  }

  // A tab left open past expiry has to come back into view before anyone can click the
  // launcher, so refreshing here usually lands before onShow would. Plain listeners, no
  // digest: the check is a local exp read and almost always a no-op.
  _listenForTabReturn() {
    if (this._tabReturnListening) {
      return;
    }
    this._tabReturnListening = true;
    this.$window.addEventListener('focus', () => this._refreshJwtIfExpiring());
    this.$document[0].addEventListener('visibilitychange', () => {
      if (this.$document[0].visibilityState === 'visible') {
        this._refreshJwtIfExpiring();
      }
    });
  }

  _refreshJwtIfExpiring() {
    const staleJwt = this.intercomSettings.intercom_user_jwt;
    if (
      !this.active ||
      this._jwtRefreshInFlight ||
      !this._isJwtExpired(staleJwt) ||
      // Bounds retries when /intercom/user is down, or when a fast device clock makes every
      // fresh token look expired.
      Date.now() - (this._lastJwtRefreshAt ?? 0) < INTERCOM_JWT_REFRESH_COOLDOWN_MS
    ) {
      return;
    }
    this._jwtRefreshInFlight = true;
    this._lastJwtRefreshAt = Date.now();
    void this._refreshJwt()
      .then(() => {
        // Never re-send the stale token: if the refresh failed, leave the session alone.
        if (this.intercomSettings.intercom_user_jwt !== staleJwt) {
          this.intercom('update', this.intercomSettings);
        }
      })
      .finally(() => {
        this._jwtRefreshInFlight = false;
      });
  }

  // Intercom gives no error when booted/updated with an expired JWT; it silently demotes the
  // user to an anonymous Lead, which skips Fin and team routing. The cached token lives for the
  // whole tab, so it must be checked before every boot/update and when the messenger opens.
  _isJwtExpired(jwt) {
    if (!jwt) {
      return false;
    }
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return (
        typeof payload.exp === 'number' &&
        payload.exp * 1000 - INTERCOM_JWT_EXPIRY_BUFFER_MS <= Date.now()
      );
    } catch (_) {
      return false;
    }
  }

  async _refreshJwt() {
    try {
      const data = await this._fetchIntercomUser();
      if (data?.intercom_user_jwt) {
        Object.assign(this.intercomSettings, {
          intercom_user_jwt: data.intercom_user_jwt,
          user_id: data.intercom_user_id,
        });
      }
    } catch (_) {
      console.warn('[Intercom] Failed to refresh expired JWT');
    }
  }

  _fetchIntercomUser() {
    const $http = this.$injector.get('$http');
    return $http
      .get(`${this.Authentication.baseUrl}intercom/user`, {
        headers: { Authorization: this.Authentication.token },
        ignoreGlobalErrorHandler: true,
      })
      .then((r) => r.data ?? null);
  }

  async init() {
    if (shouldImpersonate()) {
      return;
    }
    // Booted = messenger already initialized; in-progress = mutex for this async init (not redundant).
    if (this.$window.__slingIntercomBooted || this.$window.__slingIntercomInitInProgress) {
      return;
    }

    this.$window.__slingIntercomInitInProgress = true;
    try {
      await this._performIntercomInit();
    } catch (e) {
      if (e && e.name === 'IntercomToastBootUnavailableError') {
        console.warn('[Intercom]', e.message);
        return;
      }
      throw e;
    } finally {
      this.$window.__slingIntercomInitInProgress = false;
    }
  }

  // eslint-disable-next-line complexity
  async _performIntercomInit() {
    const user = this.Authentication.user;
    if (!user) {
      return;
    }

    let flags = { intercomToastEnabled: true, alwaysCallIntercomEndpoint: false };
    try {
      flags = await getIntercomBootSettingsFlags(user.org?.id);
    } catch (_) {
      // LD unavailable → use defaults (true, false)
    }

    // Legacy boot always needs config app id; Toast gets workspace id from /intercom/user.
    if (!flags.intercomToastEnabled && !config.INTERCOM_APP_ID) {
      return;
    }

    const settings = await getIntercomBootSettings({
      flags,
      defaultAppId: config.INTERCOM_APP_ID,
      user: {
        fullname: `${user.name} ${user.lastname}`,
        email: user.email,
        id: user.id,
        org: user.org,
        status: user.status,
        isOrgCreator: user.isOrgCreator,
      },
      getIntercomUser: () => this._fetchIntercomUser(),
      getSession: () =>
        this.$q.resolve({
          intercomUserHash: this.Authentication.intercomUserHash ?? undefined,
        }),
    });

    // Toast path: boot with full JWT settings up front.
    // Legacy path: boot with app_id only; updateUser populates identity fields (user_id=email, user_hash).
    this.intercomSettings = flags.intercomToastEnabled
      ? { ...this.intercomSettings, ...settings }
      : { app_id: settings.app_id };
    const appId = settings.app_id;

    // Toast path: set full JWT settings so the initial auto-boot is already authenticated.
    // Legacy path: visitor-only boot; identity is added later via updateUser.
    this.$window.intercomSettings = flags.intercomToastEnabled
      ? { ...this.intercomSettings }
      : { app_id: appId };

    intercomScript(appId);

    // ECPL-6501: observability only — must never disrupt the Intercom boot, so the whole
    // gate-and-emit is wrapped. See _maybeReportLegacyBoot.
    this._maybeReportLegacyBoot(appId, flags, user);

    const bootOrRegisterOnShow = () => {
      if (flags.intercomToastEnabled) {
        // Boot immediately with JWT — no visitor phase, no double messenger.
        if (!this.active) {
          this.activate();
        }
      } else {
        // Legacy path: visitor launcher is already visible; upgrade to identified user on first open.
        this.intercom('onShow', () => {
          if (this.Authentication.user) {
            this.Authentication.user.settings.clientFlags.registeredInIntercom = new Date();
            this.Authentication.syncSettings();
          }

          if (!this.active) {
            this.activate();
            this.intercom('show');
          }
        });
      }
    };

    // intercomScript's stub sets window.Intercom.q; the real SDK does not have .q.
    // After logout and re-login the SDK stays loaded, so scr.onload won't fire again —
    // detect this and invoke the boot callback directly to avoid it never being set up.
    if (this._isSdkLoaded()) {
      bootOrRegisterOnShow();
    } else {
      onIntercomScriptLoad(bootOrRegisterOnShow);
    }

    this.Authentication.onOrgUpdate(() =>
      this.updateUser(this.Authentication.user, this.Authentication.intercomUserHash)
    );

    // Toast JWT boot already includes full settings — skip updateUser. Otherwise (toast
    // disabled, or toast enabled but JWT fetch failed and fell back to legacy boot) run
    // updateUser to populate identity and the full metadata Care needs. The JWT failure
    // case still gets identity from the legacy fallback in getIntercomBootSettings; this
    // adds the additional metadata on top.
    if (this._intercomToastJwtBootFailedOrDisabled(flags)) {
      void this.updateUser(user, this.Authentication.intercomUserHash);
    }
  }

  _intercomToastJwtBootFailedOrDisabled(flags) {
    return !flags.intercomToastEnabled || !this.intercomSettings.intercom_user_jwt;
  }

  // True once the real Intercom SDK has replaced intercomScript's stub (stub exposes `.q`).
  _isSdkLoaded() {
    return typeof this.$window.Intercom === 'function' && !('q' in this.$window.Intercom);
  }

  // ECPL-6501: gate + emit the legacy-selection signal, fully guarded. This is observability
  // only and runs mid-boot inside _performIntercomInit, so any throw here (gate, Sentry, state
  // access) MUST NOT propagate or it would abort the rest of the Intercom boot. Swallow + warn.
  _maybeReportLegacyBoot(appId, flags, user) {
    try {
      if (this._isLegacyWorkspaceSelected(appId, flags)) {
        this._reportLegacyBoot({ appId, flags, user });
      }
    } catch (e) {
      console.warn('[Intercom] legacy-selection telemetry failed', e);
    }
  }

  // ECPL-6501: true when this init selected the legacy Sling workspace, not Toast. The booted
  // app_id is the default (Sling) workspace id AND Toast is off or its JWT is missing. The second
  // clause avoids a false positive when a successful Toast JWT response omits intercom_app_id
  // (getIntercomBootSettings then falls back to defaultAppId while still being a real Toast boot).
  _isLegacyWorkspaceSelected(appId, flags) {
    return appId === config.INTERCOM_APP_ID && this._intercomToastJwtBootFailedOrDisabled(flags);
  }

  // ECPL-6501: sampled, observability-only signal that web selected the legacy Sling Intercom
  // workspace rather than Toast (the migration intends Toast). Fires at workspace-selection time
  // (script injection), counting legacy *exposure* per init(), not a confirmed messenger open.
  // Sampled to protect the Sentry error budget; scale counts by 1/INTERCOM_LEGACY_BOOT_SAMPLE_RATE
  // (~50× at 0.02) to estimate true selection volume. No PII — only org id, booted app id, flag.
  _reportLegacyBoot({ appId, flags, user }) {
    if (Math.random() >= INTERCOM_LEGACY_BOOT_SAMPLE_RATE) {
      return;
    }
    Sentry.captureMessage('[Intercom] Legacy Sling workspace selected', {
      level: 'info',
      tags: {
        intercom_workspace: 'legacy_sling',
        intercom_toast_enabled: String(!!flags.intercomToastEnabled),
      },
      extra: {
        sling_org_id: user.org?.id,
        booted_app_id: appId,
        intercom_toast_enabled: !!flags.intercomToastEnabled,
        sample_rate: INTERCOM_LEGACY_BOOT_SAMPLE_RATE,
      },
    });
  }

  // eslint-disable-next-line complexity
  async updateUser(user, userHash) {
    const additionalData = await this._getIntercomAdditionalData(user);
    let flags = { intercomToastEnabled: true, alwaysCallIntercomEndpoint: false };
    try {
      flags = await getIntercomBootSettingsFlags(user?.org?.id);
    } catch (_) {
      // LD unavailable → use defaults (true, false)
    }

    if (flags.intercomToastEnabled || flags.alwaysCallIntercomEndpoint) {
      const alreadyHaveJwtForUser =
        this.intercomSettings.intercom_user_jwt &&
        !this._isJwtExpired(this.intercomSettings.intercom_user_jwt) &&
        this.intercomSettings.user_sling_id === user?.id;
      if (flags.intercomToastEnabled && alreadyHaveJwtForUser) {
        Object.assign(this.intercomSettings, {
          name: `${user.name} ${user.lastname}`,
          user_sling_id: user.id,
          org_id: user.org?.id,
          email: user.email,
          created_at: user.org?.createdAt
            ? Math.floor(new Date(user.org.createdAt).getTime() / 1000)
            : undefined,
          user_status: user.status,
          is_org_creator: user.isOrgCreator,
          ...additionalData,
        });
        this.intercom('update', this.intercomSettings);
        return;
      }
      try {
        const data = await this._fetchIntercomUser();
        // Only use the JWT response when intercomToastEnabled is true. When alwaysCallIntercomEndpoint
        // is true but toast is off, we still call the endpoint (per the outer condition) but must fall
        // through to legacy settings — the JWT path is reserved for the Toast workspace.
        if (flags.intercomToastEnabled && data?.intercom_user_jwt) {
          const appId = data.intercom_app_id || config.INTERCOM_APP_ID;
          Object.assign(this.intercomSettings, {
            app_id: appId,
            name: `${user.name} ${user.lastname}`,
            user_id: data.intercom_user_id,
            org_id: user.org.id,
            email: user.email,
            created_at: Math.floor(new Date(user.org.createdAt).getTime() / 1000),
            user_status: user.status,
            is_org_creator: user.isOrgCreator,
            intercom_user_jwt: data.intercom_user_jwt,
            ...(data.user_data || {}),
            // user_data carries user_sling_id as a string; keep the numeric id so the
            // alreadyHaveJwtForUser === check still matches on the next update.
            user_sling_id: user.id,
            ...additionalData,
          });
          delete this.intercomSettings.user_hash;
          this.intercom('update', this.intercomSettings);
          return;
        }
      } catch (_) {
        console.warn('[Intercom] Failed to update user settings, falling back to legacy settings');
      }
    }

    Object.assign(this.intercomSettings, {
      name: `${user.name} ${user.lastname}`,
      user_id: user.email,
      user_sling_id: user.id,
      org_id: user.org.id,
      email: user.email,
      created_at: Math.floor(new Date(user.org.createdAt).getTime() / 1000),
      user_status: user.status,
      user_hash: userHash,
      is_org_creator: user.isOrgCreator,
      ...additionalData,
    });

    this.intercom('update', this.intercomSettings);
  }

  async _getIntercomAdditionalData(user) {
    if (!user) {
      return {};
    }

    const PersonaCollection = this.$injector.get('PersonaCollection');
    const [billingInfo] = await this.$q.all([
      this._getSubscriptionInfo(),
      PersonaCollection.loadData(),
    ]);

    const { isPremium, premiumTrialStartDate, subscriptionStartDate, nextChargeDate } =
      this.Authentication.user.org;

    const SubscriptionUtils = this.$injector.get('SubscriptionUtils');
    const isOnTrial = isPremium && premiumTrialStartDate && !subscriptionStartDate;
    const subscriptionName = SubscriptionUtils.getSubscriptionNameDetailed(
      user.org.subscriptionLevel,
      isOnTrial
    );
    const activeUsers = PersonaCollection.allUsers.filter(prop('active'));
    const activeLocations = PersonaCollection.locations;
    const trialEndDate = isOnTrial ? nextChargeDate : undefined;
    const trialEndDateString =
      trialEndDate &&
      `${this.Utils.formatDate(trialEndDate)} ${this.Utils.formatTime(trialEndDate, true)}`;

    return {
      user_role: getUserRoleName(user.role),
      subscription_plan: subscriptionName,
      trial_end_date: trialEndDateString,
      business_category: user.org.businessCategory,
      user_timezone: user.timezone,
      next_charge_amount: billingInfo?.nextChargeAmount,
      active_users_count: activeUsers.length,
      active_location_count: activeLocations.length,
      free_private_messages:
        !this.Authentication.isOrgFeatureEnabled('groupMessages') &&
        this.Authentication.isOrgFeatureEnabled('privateMessages'),
    };
  }

  _getSubscriptionInfo() {
    const AccountService = this.$injector.get('AccountService');
    if (
      this.Authentication.user?.orgSettings.subscriptionOptionsEnabled &&
      this.Authentication.user.isAllowedToManageSubscription
    ) {
      return AccountService.getBillingInfo({ fromCache: true });
    }
  }

  async reset() {
    let flags = { intercomToastEnabled: true, alwaysCallIntercomEndpoint: false };
    try {
      flags = await getIntercomBootSettingsFlags(this.Authentication.user?.org?.id);
    } catch (_) {
      // LD unavailable → use defaults (true, false)
    }

    if (!flags.intercomToastEnabled) {
      // Legacy Intercom: stay loaded; anonymize the visitor.
      Object.assign(this.intercomSettings, {
        name: 'Anonymous',
        user_id: this._getAnonymousIntercomUserId(),
        email: null,
      });
      if (this.active) {
        this.intercom('update', this.intercomSettings);
      }
    } else {
      // Toast Intercom: full teardown; next session re-fetches /intercom/user on init.
      try {
        this.intercom('shutdown');
      } catch (_) {
        // noop: stub or SDK error
      }
      this.$window.__slingIntercomBooted = false;
      this.$window.__slingIntercomInitInProgress = false;
      this.active = false;
      this.intercomSettings = {};
      delete this.$window.intercomSettings;
    }
    // Reset boot flags so init() re-runs on next login and re-sends user attributes.
    this.$window.__slingIntercomBooted = false;
    this.$window.__slingIntercomInitInProgress = false;
    this.active = false;
  }

  _getAnonymousIntercomUserId() {
    let id = this.$sessionStorage._anonymousIntercomUserId;
    if (!id) {
      id = this.uuid.new();
      this.$sessionStorage._anonymousIntercomUserId = id;
    }

    return id;
  }
}
